import { useEffect } from 'react'

import { acceptAllViaCookieYes } from 'utils/cookieYesConsent'

const CONTENT_SELECTOR = '.cky-preference-content-wrapper'
const SWITCH_SELECTOR = '.cky-preference-center .cky-switch input[type="checkbox"]'
const SETTINGS_TRIGGER_SELECTOR = '.cky-btn-customize, [data-cky-tag="settings-button"]'
const BUTTON_ID = 'cky-custom-accept-all-btn'
const BUTTON_LABEL = 'Tümüne İzin Ver'
const WRAPPER_CLASS = 'cky-custom-accept-all-wrapper'
const ACTIVE_SWITCH_COLOR = '#1578F7'
const RETRY_INTERVAL_MS = 500
const RETRY_MAX_MS = 15000
const SETTINGS_SYNC_DELAYS_MS = [0, 150, 400]

const enableAllCategoryToggles = (): void => {
  document.querySelectorAll<HTMLInputElement>(SWITCH_SELECTOR).forEach((checkbox) => {
    if (checkbox.disabled || checkbox.checked) return
    checkbox.checked = true
    checkbox.style.backgroundColor = ACTIVE_SWITCH_COLOR
    checkbox.dispatchEvent(new Event('change', { bubbles: true }))
  })
}

const isButtonInjected = (): boolean => {
  const button = document.getElementById(BUTTON_ID)
  return !!button && button.isConnected
}

export const injectAcceptAllButton = (): boolean => {
  const contentWrapper = document.querySelector(CONTENT_SELECTOR)
  if (!contentWrapper || isButtonInjected()) return false

  const staleWrapper = contentWrapper.nextElementSibling
  if (staleWrapper?.classList.contains(WRAPPER_CLASS)) staleWrapper.remove()

  const wrapper = document.createElement('div')
  wrapper.className = WRAPPER_CLASS

  const button = document.createElement('button')
  button.id = BUTTON_ID
  button.className = 'cky-btn cky-custom-accept-all-btn'
  button.type = 'button'
  button.setAttribute('aria-label', BUTTON_LABEL)
  button.textContent = BUTTON_LABEL
  button.addEventListener('click', (event) => {
    event.preventDefault()
    event.stopPropagation()
    enableAllCategoryToggles()
    acceptAllViaCookieYes()
  })

  wrapper.appendChild(button)
  contentWrapper.insertAdjacentElement('afterend', wrapper)
  return true
}

/**
 * CookieYes tercih merkezine "Tümüne İzin Ver" butonu ekler (enuygunsigorta.com ile aynı tasarım;
 * stiller CookieYes panelindeki custom CSS'ten gelir). kasko-nextjs `useCookieYesAcceptAllButton` portu.
 */
const useCookieYesAcceptAllButton = (enabled: boolean): void => {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined

    const timeouts: number[] = []
    const syncButton = (): void => {
      injectAcceptAllButton()
    }

    const handleSettingsClick = (event: Event): void => {
      const target = event.target
      if (!(target instanceof Element) || !target.closest(SETTINGS_TRIGGER_SELECTOR)) return
      SETTINGS_SYNC_DELAYS_MS.forEach((delay) => timeouts.push(window.setTimeout(syncButton, delay)))
    }

    const observer = new MutationObserver(syncButton)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] })

    document.addEventListener('cookieyes_banner_load', syncButton)
    document.addEventListener('click', handleSettingsClick, true)

    syncButton()

    const retryInterval = window.setInterval(() => {
      if (document.querySelector(CONTENT_SELECTOR)) syncButton()
    }, RETRY_INTERVAL_MS)
    const retryStop = window.setTimeout(() => window.clearInterval(retryInterval), RETRY_MAX_MS)

    return () => {
      observer.disconnect()
      document.removeEventListener('cookieyes_banner_load', syncButton)
      document.removeEventListener('click', handleSettingsClick, true)
      window.clearInterval(retryInterval)
      window.clearTimeout(retryStop)
      timeouts.forEach((id) => window.clearTimeout(id))
    }
  }, [enabled])
}

export default useCookieYesAcceptAllButton
